const parseResponseBody = async (response) => {
	try {
		const text = await response.text();
		if (!text) return null;
		return JSON.parse(text);
	} catch (err) {
		return null;
	}
};

const shouldLogMultipartUploads =
	typeof process !== "undefined" &&
	process?.env?.NODE_ENV !== "production";

const CHUNK_UPLOAD_TIMEOUT_MS = 120000; // 2 minutes per part
const COMMIT_TIMEOUT_MS = 60000; // 1 minute to finalize

const fetchWithTimeout = async (url, options, timeoutMs, timeoutLabel) => {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

	try {
		return await fetch(url, { ...options, signal: controller.signal });
	} catch (err) {
		if (err?.name === "AbortError") {
			throw new Error(
				`${timeoutLabel} timed out after ${Math.round(timeoutMs / 1000)}s`
			);
		}
		throw new Error(`${timeoutLabel} failed: ${err?.message || err}`);
	} finally {
		clearTimeout(timeoutId);
	}
};

export const uploadMultipartFile = async ({
	file,
	chunkList,
	commitUrl,
	authorization,
}) => {
	if (!file) {
		throw new Error("A file is required for multipart upload");
	}

	if (!Array.isArray(chunkList) || chunkList.length === 0) {
		throw new Error("Multipart upload chunk list is missing");
	}

	let offset = 0;
	if (shouldLogMultipartUploads) {
		console.log("[Multipart Upload] starting direct part uploads", {
			fileName: file?.name,
			fileSize: Number(file?.size ?? 0),
			partCount: chunkList.length,
		});
	}

	for (let index = 0; index < chunkList.length; index += 1) {
		const chunk = chunkList[index];
		const chunkSize = Number(chunk?.size ?? 0);
		const end = chunkSize > 0 ? Math.min(offset + chunkSize, file.size) : file.size;
		const chunkBlob = file.slice(offset, end, file?.type);

		if (shouldLogMultipartUploads) {
			console.log("[Multipart Upload] part request", {
				fileName: file?.name,
				chunkIndex: index + 1,
				chunkSize,
				actualChunkBytes: chunkBlob?.size ?? 0,
				start: offset,
				end,
				uploadUrl: chunk?.uploadUrl,
			});
		}

		const response = await fetchWithTimeout(
			chunk?.uploadUrl,
			{ method: "PUT", body: chunkBlob },
			CHUNK_UPLOAD_TIMEOUT_MS,
			`Multipart chunk ${index + 1}/${chunkList.length} upload`
		);

		if (shouldLogMultipartUploads) {
			console.log("[Multipart Upload] part response", {
				fileName: file?.name,
				chunkIndex: index + 1,
				status: response.status,
				ok: response.ok,
			});
		}

		if (!response.ok) {
			const errorBody = await response.text().catch(() => "");
			throw new Error(
				`Multipart chunk ${index + 1}/${chunkList.length} upload failed: status=${response.status}${
					errorBody ? ` body=${errorBody.slice(0, 500)}` : ""
				}`
			);
		}

		offset = end;
	}

	if (commitUrl) {
		if (shouldLogMultipartUploads) {
			console.log("[Multipart Upload] committing upload", {
				fileName: file?.name,
				commitUrl,
			});
		}

		const commitResponse = await fetchWithTimeout(
			commitUrl,
			{ method: "POST" },
			COMMIT_TIMEOUT_MS,
			"Multipart upload commit"
		);

		if (!commitResponse.ok) {
			const errorBody = await commitResponse.text().catch(() => "");
			throw new Error(
				`Multipart upload commit failed: status=${commitResponse.status}${
					errorBody ? ` body=${errorBody.slice(0, 500)}` : ""
				}`
			);
		}

		return (await parseResponseBody(commitResponse)) ?? { status: "success" };
	}

	return { status: "success" };
};
